/* eslint-disable no-unused-vars */
import { getFirestore, setDoc, doc, Timestamp, collection } from "firebase/firestore";
import { app } from "../config"
import { getDownloadURL, ref, uploadBytes, getStorage } from "firebase/storage";

// const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage();

// Function to check if the incoming object has all the necessary fields
const hasAllRequiredFields = (object) => {
  // Required Fields
  const requiredFields = [
    "userId",
    "companyLogo",
    "businessName",
    "email",
    "phoneNumber",
    "description",
    "socialMediaLinks",
    "website",
    "typeOfBusiness",
    "documents",
    "signedTermsOfService"
  ];

  // for (const field of requiredFields) {
  //   if (!object.hasOwnProperty(field) || object[field] === null || object[field] === undefined || object[field] === "") {
  //     return { success: false, message: `Missing or invalid value for required field: ${field}` };
  //   }
  // }
  return { success: true };
};

// Validation Functions
const isPhoneNumberValid = (phoneNumber) => {
  const re = /^\+?1?\s*\(?[2-9]\d{2}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/;
  return re.test(phoneNumber);
};

const isEmailValid = (email) => {
  const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return re.test(email);
};

const isWebsiteValid = (website) => {
  try {
    new URL(website); // If URL is not valid, this will throw an error
    return true;
  } catch (error) {
    return false;
  }
};

const createMPSBusinessProfileInFirestore = async (MPS) => {
  try {

    let companyLogoURL = MPS?.companyLogo;
    // Handle companyLogo upload
    if (MPS.companyLogo?.file instanceof File) {
      const timestamp = new Date().toISOString();
      const file = MPS.companyLogo.file;
      const fileName = `${file.name}_${timestamp}`;
      const storageRef = ref(storage, `users/${MPS.userId}/company_logos/${fileName}`);
      
      try {
        await uploadBytes(storageRef, MPS.companyLogo.file);
        companyLogoURL = await getDownloadURL(storageRef);
      } catch (error) {
        console.error("Error uploading company logo:", error);
        // If logo upload fails, continue with profile creation but use default logo
        companyLogoURL = null;
      }
    }

    // Handle documents upload
    if (MPS.documents && Array.isArray(MPS.documents)) {
      const updatedDocuments = await Promise.all(
        MPS.documents.map(async (doc) => {
          if (doc.file && doc.fileName) {
            const timestamp = new Date().toISOString();
            const fileName = `${doc.fileName}_${timestamp}`;
            const storageRef = ref(storage, `users/${MPS.userId}/documents/${fileName}`);
            try {
              const uploadTaskSnapshot = await uploadBytes(storageRef, doc.file);
              const downloadURL = await getDownloadURL(uploadTaskSnapshot.ref);
              return { ...doc, file: downloadURL }; // Replace the file with the URL
            } catch (error) {
              console.error("Error uploading document:", error);
              return { ...doc, file: null }; // Return document without file if upload fails
            }
          }
          return doc; // Return document as-is if no file exists
        })
      );
      MPS.documents = updatedDocuments; // Update the documents in MPS
    }

    // Prepare the MPS business profile document
    const MPSBusinessProfile = {
      ...MPS,
      companyLogo: companyLogoURL,
      createdAt: MPS.createdAt || Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    let profileRef;

    // Check if the document exists
    if (MPS.userId) {
      profileRef = doc(db, "MPS_BusinessProfiles", MPS.userId);
    } else {
      profileRef = doc(collection(db, "MPS_BusinessProfiles"));
      MPSBusinessProfile.userId = profileRef.id;
    }

    // Set or update the document in Firestore
    await setDoc(profileRef, MPSBusinessProfile, { merge: true });

    // IMPORTANT: Also update the user document to mark MPS as signed up
    if (MPS.userId) {
      const userRef = doc(db, "users", MPS.userId);
      await setDoc(userRef, { 
        isMpsSignedUp: true,
        signedInAs: 'MPS',
        updatedAt: Timestamp.now()
      }, { merge: true });
    }

    return { success: true, userId: profileRef.id };
  } catch (error) {
    console.error("Error creating MPS business profile: ", error);
    return { success: false, error: error.message };
  }
};


// Function to Create MPS Business Profile with Checks
const createMPSBusinessProfile = async (request) => {
  try {
    const MPSData = request;

    // Check if all required fields are present and valid
    const { success, message } = hasAllRequiredFields(MPSData);
    if (!success) {
      throw new Error(message);
    }

    // Validate phone number
    if (!isPhoneNumberValid(MPSData.phoneNumber)) {
      throw new Error("Invalid phone number. It should be exactly 10 digits.");
    }

    // Validate email
    if (!isEmailValid(MPSData.email)) {
      throw new Error("Invalid email. It should include an '@' symbol and be in a valid format.");
    }

    // Validate signed terms of service
    if (MPSData.signedTermsOfService !== true) {
      throw new Error("Terms of Service must be signed.");
    }

    // Validate website URL
    if (MPSData.website && !isWebsiteValid(MPSData.website)) {
      throw new Error("Invalid website URL.");
    }

    // Create the profile in Firestore
    await createMPSBusinessProfileInFirestore(MPSData);
    return { success: true, message: "MPS Business Profile Created" }
  } catch (error) {
    console.error("Error processing the request: ", error);
    return { success: false, message: error.message };
  }
};



export { createMPSBusinessProfile };
